import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Plus, Trash2, Pencil, Palette, Crown, ChevronDown, ChevronUp, X } from "lucide-react";
import { Switch } from "./ui/switch";
import EmojiPicker from "./EmojiPicker";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { useMetricDefinitions } from "@/lib/useMetricDefinitions";
import { useTheme } from "@/providers/ThemeProvider";
import { MetricsTour } from "@/components/auth/mobile/MetricsTour";
import ScreenshotImportSection, { ImportedDayEntry } from "./ScreenshotImportSection";

interface CustomMetricsDialogProps {
  userId: string;
  iconOnly?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  onClose?: () => void; // Callback to close parent dialog
  initialValues?: {
    name: string;
    emoji: string;
    unit: string;
    goal: string;
    inverse: boolean;
    metricType: "numeric" | "boolean";
    isAdditive?: boolean;
    isRelative?: boolean;
  };
  editMode?: boolean; // When true, only shows edit form without create button or existing metrics list
  onSaveEdit?: (editedValues: {
    name: string;
    emoji: string;
    unit: string;
    goal: string;
    inverse: boolean;
    metricType: "numeric" | "boolean";
    isAdditive?: boolean;
    isRelative?: boolean;
  }) => void; // Callback when saving edits in edit mode
  onCreate?: (newMetric: {
    key: string;
    name: string;
    emoji: string;
    unit: string;
    type: "numeric" | "boolean";
    goal: number;
    value: number;
    inverse: boolean;
    isAdditive?: boolean;
    isRelative?: boolean;
  }) => void; // Callback when creating a new metric
  hideColorPicker?: boolean; // When true, hides the color picker section (for anonymous/onboarding)
  existingCustomMetrics?: Array<{ // For anonymous/onboarding mode - pass existing metrics to show correct count
    metric_key: string;
    metric_name: string;
    metric_type: string;
  }>;
}

// Examples extracted to translation.json

// Stable default values to prevent useEffect re-fires from new references each render
const EMPTY_CUSTOM_METRICS: any[] = [];
const EMPTY_METRIC_DEFINITIONS: Record<string, any> = {};

const CustomMetricsDialog = ({ userId, iconOnly = false, onOpenChange, open: externalOpen, onClose, initialValues, editMode = false, onSaveEdit, onCreate, hideColorPicker = false, existingCustomMetrics }: CustomMetricsDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (externalOpen === undefined) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const PLACEHOLDER_EXAMPLES = t('components.custom_metrics_dialog.examples.numeric', { returnObjects: true }) as string[];
  const UNIT_EXAMPLES = t('components.custom_metrics_dialog.examples.units', { returnObjects: true }) as string[];
  const BOOLEAN_EXAMPLES = t('components.custom_metrics_dialog.examples.boolean', { returnObjects: true }) as string[];
  const COMBINED_EXAMPLES = t('components.custom_metrics_dialog.examples.combined', { returnObjects: true }) as string[];
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // Progressive disclosure state - track which steps have been revealed
  const [hasTypedName, setHasTypedName] = useState(false);
  const [hasSelectedType, setHasSelectedType] = useState(false);
  const [hasEnteredUnit, setHasEnteredUnit] = useState(false);
  const [hasInteractedWithGoal, setHasInteractedWithGoal] = useState(false);
  const [typeSelection, setTypeSelection] = useState<"numeric" | "boolean" | null>(null);

  // Auto-reveal timers - reveal next section after 1s even if user hasn't interacted
  useEffect(() => {
    if (hasEnteredUnit && !hasInteractedWithGoal) {
      const timer = setTimeout(() => {
        setHasInteractedWithGoal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasEnteredUnit, hasInteractedWithGoal]);

  const { data: customMetrics = EMPTY_CUSTOM_METRICS } = useQuery({
    queryKey: ["custom-metrics", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", userId)
        .order("metric_key");
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId, // Only run query if userId exists
  });

  const { data: profile } = useProfile(userId);
  const { data: metricDefinitions = EMPTY_METRIC_DEFINITIONS } = useMetricDefinitions();

  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("created_at")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId, // Only run query if userId exists
  });

  const isPremium = profile?.is_premium ?? false;

  // Calculate if user is within 7-day trial period
  const createdAt = userProfile?.created_at ? new Date(userProfile.created_at) : null;
  const daysSinceCreation = createdAt
    ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
    : 999; // Default to high number if unknown (assume past trial)
  const isInTrialPeriod = daysSinceCreation < 7;

  // Use existingCustomMetrics if provided (for anonymous/onboarding), otherwise use database query
  const metricsToUse = existingCustomMetrics || customMetrics;

  // During onboarding (no userId), allow up to 10 custom metrics
  // For logged-in users: Premium or within trial period get 10, otherwise 0
  const MAX_CUSTOM_METRICS = !userId ? 10 : (isPremium || isInTrialPeriod ? 10 : 0);
  const availableSlots = Array.from({ length: MAX_CUSTOM_METRICS }, (_, i) => `custom_${i + 1}`);
  const usedSlots = new Set(metricsToUse.map(m => m.metric_key));
  const nextAvailableSlot = availableSlots.find(slot => !usedSlots.has(slot));

  const [newMetric, setNewMetric] = useState({
    name: "",
    emoji: "⭐",
    unit: "",
    goal: "100",
    hue: 142, // Default green hue
    saturation: 100, // Color vibrancy: 0 = white, 100 = fully vivid
    inverse: false, // Whether exceeding goal turns red (like calories)
    metricType: "numeric" as "numeric" | "boolean", // Type of metric
    isAdditive: true, // Whether values accumulate (true) or replace (false)
    isRelative: false, // When true, uses min/max coloring instead of goal-based
    presetType: "none" as "none" | "red-to-green" | "green-to-red", // Color preset type
  });

  // Color presets for gradient options
  const redToGreenPreset: Record<number, string> = {
    0: "hsl(var(--muted))",
    1: "#ef4444",
    2: "#f97316",
    3: "#eab308",
    4: "#22c55e",
  };

  const greenToRedPreset: Record<number, string> = {
    0: "hsl(var(--muted))",
    1: "#22c55e",
    2: "#eab308",
    3: "#f97316",
    4: "#ef4444",
  };

  const [isAddMetricExpanded, setIsAddMetricExpanded] = useState(true);
  const [metricToDelete, setMetricToDelete] = useState<string | null>(null);
  const [editingMetricKey, setEditingMetricKey] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [hasUnsavedMetricWarning, setHasUnsavedMetricWarning] = useState(false);
  const [showMetricsTour, setShowMetricsTour] = useState(false);
  const [metricsScrollTop, setMetricsScrollTop] = useState(0);
  const [metricsScrollHeight, setMetricsScrollHeight] = useState(0);
  const [metricsClientHeight, setMetricsClientHeight] = useState(0);
  const [importedScreenshotData, setImportedScreenshotData] = useState<ImportedDayEntry[] | null>(null);
  const metricsScrollRef = useRef<HTMLDivElement>(null);

  // Cycle through placeholder examples - use COMBINED when no type selected
  useEffect(() => {
    if (open && isAddMetricExpanded) {
      const examples = !hasSelectedType ? COMBINED_EXAMPLES : (typeSelection === "boolean" ? BOOLEAN_EXAMPLES : PLACEHOLDER_EXAMPLES);
      const interval = setInterval(() => {
        setPlaceholderIndex((prev) => (prev + 1) % examples.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [open, isAddMetricExpanded, hasSelectedType, typeSelection]);

  // Reset progressive disclosure state when dialog opens (for normal mode only)
  useEffect(() => {
    if (open && !editMode) {
      setHasTypedName(false);
      setHasSelectedType(false);
      setHasEnteredUnit(false);
      setHasInteractedWithGoal(false);
      setTypeSelection(null);
      setPlaceholderIndex(0);
      setEditingMetricKey(null);
    }
  }, [open, editMode]);

  // Populate form with initial values when provided
  useEffect(() => {
    if (initialValues && open) {
      // If goal is empty/null or isRelative is true, start with collapsed state (isRelative: true)
      const shouldBeRelative = initialValues.isRelative || !initialValues.goal || initialValues.goal === "";

      setNewMetric({
        name: initialValues.name,
        emoji: initialValues.emoji,
        unit: initialValues.unit,
        goal: initialValues.goal || "100", // Default to 100 if empty
        hue: 142, // Keep default hue
        saturation: 100, // Keep default saturation
        inverse: initialValues.inverse,
        metricType: initialValues.metricType,
        isAdditive: initialValues.isAdditive ?? true,
        isRelative: shouldBeRelative,
        presetType: "none",
      });
      setIsAddMetricExpanded(true); // Expand the form
    }
  }, [initialValues, open]);

  // Set default emoji to one not already used
  useEffect(() => {
    if (open && !initialValues) {
      // Get all used emojis from built-in metrics and custom metrics
      const usedEmojis = new Set<string>();

      // Add emojis from built-in metrics
      Object.values(metricDefinitions).forEach((metric) => {
        if (metric.emoji) usedEmojis.add(metric.emoji);
      });

      // Add emojis from custom metrics (saved to database)
      customMetrics.forEach((metric) => {
        if (metric.metric_emoji) usedEmojis.add(metric.metric_emoji);
      });

      // Add emojis from existing custom metrics (temporary metrics passed in, e.g., from Update Today's Totals dialog)
      if (existingCustomMetrics) {
        existingCustomMetrics.forEach((metric) => {
          if ((metric as any).metric_emoji) {
            usedEmojis.add((metric as any).metric_emoji);
          }
        });
      }

      // Common emojis to choose from
      const availableEmojis = [
        "⭐", "🎯", "💪", "🏃", "🚴", "🏋️", "🧘", "🥗", "🍎", "💧",
        "😴", "📚", "🎨", "🎵", "🎮", "☕", "🌱", "🌞", "🌙", "⚡",
        "🔥", "💎", "🎪", "🎭", "🎬", "📝", "✏️", "🖊️", "📖", "📊",
        "📈", "🏆", "🥇", "🎖️", "🏅", "🎁", "🎉", "🎊", "🎈", "🌈",
        "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏓", "🥊", "🥋", "⛷️",
      ];

      // Find first unused emoji
      const unusedEmoji = availableEmojis.find(emoji => !usedEmojis.has(emoji)) || "⭐";

      setNewMetric((prev) => ({ ...prev, emoji: unusedEmoji }));
    }
  }, [open, initialValues, metricDefinitions, customMetrics, existingCustomMetrics]);

  // Generate color palette from hue and saturation
  // saturation: 0 = white, 100 = fully vivid
  const generateColorPalette = (hue: number, sat: number = 100): string[] => {
    const hslToHex = (h: number, s: number, l: number) => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    // Map saturation slider (0-100) to HSL values:
    // sat=0 → white (s=0%, l=100%), sat=100 → vivid (s=100%, l=50%)
    const cssSaturation = sat;
    const cssLightness = 100 - (sat / 2);
    const baseHex = hslToHex(hue, cssSaturation, cssLightness);
    return [
      "hsl(var(--muted))",
      `color-mix(in srgb, ${baseHex} 25%, hsl(var(--muted)))`,
      `color-mix(in srgb, ${baseHex} 50%, hsl(var(--muted)))`,
      `color-mix(in srgb, ${baseHex} 75%, hsl(var(--muted)))`,
      baseHex,
    ];
  };

  // Get current color palette based on preset or hue
  const getCurrentColorPalette = (): string[] => {
    if (newMetric.presetType === "red-to-green") {
      return [redToGreenPreset[0], redToGreenPreset[1], redToGreenPreset[2], redToGreenPreset[3], redToGreenPreset[4]];
    } else if (newMetric.presetType === "green-to-red") {
      return [greenToRedPreset[0], greenToRedPreset[1], greenToRedPreset[2], greenToRedPreset[3], greenToRedPreset[4]];
    }
    return generateColorPalette(newMetric.hue, newMetric.saturation);
  };

  const saveMetric = useMutation({
    mutationFn: async (metric: { metric_key: string; metric_name: string; metric_emoji: string; metric_unit: string; is_inverse?: boolean; metric_type?: "numeric" | "boolean"; is_additive?: boolean; is_relative?: boolean }) => {
      const { error } = await supabase
        .from("custom_metrics")
        .upsert({
          user_id: userId,
          ...metric,
        });
      if (error) throw error;

      // Send push notification to followers about new graph creation
      // This bypasses notification settings and sends to all followers
      try {
        await supabase.functions.invoke('send-graph-creation-notification', {
          body: {
            userId: userId,
            graphName: metric.metric_name,
          }
        });
      } catch (notificationError) {
        console.error('Failed to send graph creation notification:', notificationError);
        // Don't fail the whole operation if notification fails
      }
    },
    // Optimistic update: immediately add the metric to the cache for instant UI feedback
    onMutate: async (newMetric) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["custom-metrics", userId] });

      // Snapshot the previous value
      const previousMetrics = queryClient.getQueryData(["custom-metrics", userId]);

      // Optimistically add the new metric to the cache
      queryClient.setQueryData(["custom-metrics", userId], (old: any[] | undefined) => {
        const optimisticMetric = {
          user_id: userId,
          ...newMetric,
          created_at: new Date().toISOString(),
        };
        if (!old) return [optimisticMetric];
        // Replace existing metric with same key (edit), or append (create)
        const existingIndex = old.findIndex(m => m.metric_key === newMetric.metric_key);
        if (existingIndex >= 0) {
          const updated = [...old];
          updated[existingIndex] = { ...old[existingIndex], ...optimisticMetric };
          return updated;
        }
        return [...old, optimisticMetric];
      });

      // Return context with the previous value for rollback
      return { previousMetrics };
    },
    onError: (_err, _newMetric, context) => {
      // Rollback to the previous value on error
      if (context?.previousMetrics) {
        queryClient.setQueryData(["custom-metrics", userId], context.previousMetrics);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync with server
      queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] });
    },
  });

  const saveGoal = useMutation({
    mutationFn: async ({ goalKey, goalValue }: { goalKey: string; goalValue: number }) => {
      const { error } = await supabase
        .from("user_goals")
        .upsert(
          {
            user_id: userId,
            [goalKey]: goalValue,
          },
          {
            onConflict: "user_id",
          }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-goals"] });
    },
  });

  const saveColorPreferences = useMutation({
    mutationFn: async ({ metricKey, colors }: { metricKey: string; colors: Record<string, string> }) => {
      // Get current color preferences
      const { data: currentPrefs } = await supabase
        .from("user_chart_preferences")
        .select("metric_colors")
        .eq("user_id", userId)
        .single();

      const existingColors = (currentPrefs?.metric_colors || {}) as Record<string, Record<string, string>>;
      const updatedColors = {
        ...existingColors,
        [metricKey]: colors,
      };

      const { error } = await supabase
        .from("user_chart_preferences")
        .upsert(
          {
            user_id: userId,
            metric_colors: updatedColors,
          },
          {
            onConflict: "user_id",
          }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chart-preferences"] });
    },
  });

  const deleteMetric = useMutation({
    mutationFn: async (metricKey: string) => {
      const { error } = await supabase
        .from("custom_metrics")
        .delete()
        .eq("user_id", userId)
        .eq("metric_key", metricKey);
      if (error) throw error;
    },
    // Optimistic update: immediately remove the metric from the cache for instant UI feedback
    onMutate: async (metricKey) => {
      await queryClient.cancelQueries({ queryKey: ["custom-metrics", userId] });
      const previousMetrics = queryClient.getQueryData(["custom-metrics", userId]);
      queryClient.setQueryData(["custom-metrics", userId], (old: any[] | undefined) => {
        return old ? old.filter(m => m.metric_key !== metricKey) : [];
      });
      return { previousMetrics };
    },
    onError: (_err, _metricKey, context) => {
      if (context?.previousMetrics) {
        queryClient.setQueryData(["custom-metrics", userId], context.previousMetrics);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] });
    },
  });

  const handleAddMetric = () => {
    // Note: The database function check_custom_metrics_limit() handles the actual enforcement
    // including the 7-day trial period. This is just a client-side pre-check.
    if (!isPremium && customMetrics.length >= 10) {
      toast.error(t('components.custom_metrics_dialog.upgrade_pro'));
      setOpen(false);
      navigate("/upgrade");
      return;
    }

    if (!nextAvailableSlot) {
      toast.error(t('components.custom_metrics_dialog.max_metrics_error', { max: MAX_CUSTOM_METRICS }));
      return;
    }
    if (!newMetric.name.trim()) {
      setValidationError(t('components.custom_metrics_dialog.name_required'));
      return;
    }

    // Check for duplicate names
    const normalizedNewName = newMetric.name.trim().toLowerCase();
    const isDuplicate = customMetrics.some(
      metric => metric.metric_name.toLowerCase() === normalizedNewName
    );
    if (isDuplicate) {
      setValidationError(t('components.custom_metrics_dialog.name_exists'));
      return;
    }

    // Check for duplicate emojis
    const isDuplicateEmoji = customMetrics.some(
      metric => metric.metric_emoji === newMetric.emoji
    );
    if (isDuplicateEmoji) {
      setValidationError(t('components.custom_metrics_dialog.emoji_exists'));
      return;
    }

    const goalValue = parseFloat(newMetric.goal);
    // Only validate goal for numeric metrics that are NOT relative
    if (newMetric.metricType === "numeric" && !newMetric.isRelative && (isNaN(goalValue) || goalValue < 0)) {
      setValidationError(t('components.custom_metrics_dialog.goal_invalid'));
      return;
    }

    // During onboarding (no userId), only use onCreate callback
    // When onCreate is provided with userId, it means we're in a flow where the parent
    // component will handle database creation (e.g., QuickEntryConfirmDialog)
    if (!userId || onCreate) {
      // Call onCreate callback if provided (for onboarding flow or deferred creation)
      if (onCreate) {
        // Ensure we always use a proper slot-based key
        if (!nextAvailableSlot) {
          toast.error(t('components.custom_metrics_dialog.max_metrics_error', { max: MAX_CUSTOM_METRICS }));
          return;
        }

        onCreate({
          key: nextAvailableSlot,
          name: newMetric.name.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 23),
          emoji: newMetric.emoji,
          unit: newMetric.metricType === "numeric" ? newMetric.unit.slice(0, 20) : "",
          type: newMetric.metricType,
          goal: goalValue,
          value: newMetric.metricType === "boolean" ? 0 : 0, // Default value
          inverse: newMetric.inverse,
          isAdditive: newMetric.isAdditive,
          isRelative: newMetric.isRelative,
        });
      }

      setNewMetric({ name: "", emoji: "⭐", unit: "", goal: "100", hue: 142, saturation: 100, inverse: false, metricType: "numeric", isAdditive: true, isRelative: false, presetType: "none" });
      setValidationError(""); // Clear validation error
      // Reset progressive disclosure states
      setHasTypedName(false);
      setHasSelectedType(false);
      setHasEnteredUnit(false);
      setHasInteractedWithGoal(false);
      setTypeSelection(null);
      setImportedScreenshotData(null);
      setOpen(false);
      return;
    }

    // Save metric to database (authenticated users only, when NOT using onCreate callback)
    saveMetric.mutate({
      metric_key: nextAvailableSlot,
      metric_name: newMetric.name.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 23),
      metric_emoji: newMetric.emoji,
      metric_unit: newMetric.metricType === "numeric" ? newMetric.unit.slice(0, 20) : "",
      is_inverse: newMetric.inverse,
      metric_type: newMetric.metricType,
      is_additive: newMetric.isAdditive,
      is_relative: newMetric.isRelative,
    });

    // Save goal only for numeric metrics that are NOT relative
    if (newMetric.metricType === "numeric" && !newMetric.isRelative) {
      const goalKey = `${nextAvailableSlot}_goal`;
      saveGoal.mutate({
        goalKey,
        goalValue,
      });
    }

    // Save color preferences if pro
    if (isPremium) {
      let colorPrefs: Record<string, string> = {};
      
      // Use preset colors if a preset is selected, otherwise generate from hue
      if (newMetric.presetType === "red-to-green") {
        Object.entries(redToGreenPreset).forEach(([idx, color]) => {
          colorPrefs[idx] = color;
        });
      } else if (newMetric.presetType === "green-to-red") {
        Object.entries(greenToRedPreset).forEach(([idx, color]) => {
          colorPrefs[idx] = color;
        });
      } else {
        const colors = generateColorPalette(newMetric.hue, newMetric.saturation);
        colors.forEach((color, idx) => {
          colorPrefs[idx.toString()] = color;
        });
      }
      
      saveColorPreferences.mutate({
        metricKey: nextAvailableSlot,
        colors: colorPrefs,
      });
    }

    // Save imported screenshot data as daily entries
    if (importedScreenshotData && importedScreenshotData.length > 0 && userId) {
      const columnName = `${nextAvailableSlot}_value`;
      // Batch upsert all imported entries
      const upsertPromises = importedScreenshotData.map((entry) =>
        supabase
          .from("daily_entries")
          .upsert(
            {
              user_id: userId,
              entry_date: entry.date,
              [columnName]: entry.value,
            },
            { onConflict: "user_id,entry_date" }
          )
      );
      // Fire and forget - don't block metric creation on data import
      Promise.all(upsertPromises)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["daily-entries"] });
        })
        .catch((err) => console.error("Failed to import screenshot data:", err));
    }

    setNewMetric({ name: "", emoji: "⭐", unit: "", goal: "100", hue: 142, saturation: 100, inverse: false, metricType: "numeric", isAdditive: true, isRelative: false, presetType: "none" });
    setValidationError(""); // Clear validation error
    setHasUnsavedMetricWarning(false); // Reset warning state
    setIsAddMetricExpanded(false); // Collapse after adding
    // Reset progressive disclosure states
    setHasTypedName(false);
    setHasSelectedType(false);
    setHasEnteredUnit(false);
    setHasInteractedWithGoal(false);
    setTypeSelection(null);
    setImportedScreenshotData(null);
  };

  const handleStartEditMetric = (metric: typeof customMetrics[number]) => {
    setEditingMetricKey(metric.metric_key);
    setNewMetric({
      name: metric.metric_name,
      emoji: metric.metric_emoji,
      unit: metric.metric_unit || "",
      goal: "100", // Default since we don't have goal data here
      hue: 142,
      saturation: 100,
      inverse: metric.is_inverse || false,
      metricType: (metric.metric_type as "numeric" | "boolean") || "numeric",
      isAdditive: metric.is_additive ?? true,
      isRelative: metric.is_relative ?? false,
      presetType: "none",
    });
    setIsAddMetricExpanded(true);
    setHasTypedName(true);
    setHasSelectedType(true);
    setTypeSelection((metric.metric_type as "numeric" | "boolean") || "numeric");
    setHasEnteredUnit(true);
    setHasInteractedWithGoal(true);
    setValidationError("");
  };

  const handleCancelEdit = () => {
    setEditingMetricKey(null);
    setNewMetric({ name: "", emoji: "⭐", unit: "", goal: "100", hue: 142, saturation: 100, inverse: false, metricType: "numeric", isAdditive: true, isRelative: false, presetType: "none" });
    setValidationError("");
    setHasTypedName(false);
    setHasSelectedType(false);
    setHasEnteredUnit(false);
    setHasInteractedWithGoal(false);
    setTypeSelection(null);
  };

  const handleSaveEditMetric = () => {
    if (!editingMetricKey) return;
    if (!newMetric.name.trim()) {
      setValidationError(t('components.custom_metrics_dialog.name_required'));
      return;
    }

    // Check for duplicate names (excluding the metric being edited)
    const normalizedNewName = newMetric.name.trim().toLowerCase();
    const isDuplicate = customMetrics.some(
      metric => metric.metric_key !== editingMetricKey && metric.metric_name.toLowerCase() === normalizedNewName
    );
    if (isDuplicate) {
      setValidationError(t('components.custom_metrics_dialog.name_exists'));
      return;
    }

    // Check for duplicate emojis (excluding the metric being edited)
    const isDuplicateEmoji = customMetrics.some(
      metric => metric.metric_key !== editingMetricKey && metric.metric_emoji === newMetric.emoji
    );
    if (isDuplicateEmoji) {
      setValidationError(t('components.custom_metrics_dialog.emoji_exists'));
      return;
    }

    saveMetric.mutate({
      metric_key: editingMetricKey,
      metric_name: newMetric.name.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 23),
      metric_emoji: newMetric.emoji,
      metric_unit: newMetric.metricType === "numeric" ? newMetric.unit.slice(0, 20) : "",
      is_inverse: newMetric.inverse,
      metric_type: newMetric.metricType,
      is_additive: newMetric.isAdditive,
      is_relative: newMetric.isRelative,
    });

    // Save goal only for numeric metrics that are NOT relative
    if (newMetric.metricType === "numeric" && !newMetric.isRelative) {
      const goalValue = parseFloat(newMetric.goal);
      if (!isNaN(goalValue) && goalValue >= 0) {
        const goalKey = `${editingMetricKey}_goal`;
        saveGoal.mutate({ goalKey, goalValue });
      }
    }

    handleCancelEdit();
    toast.success(t('components.custom_metrics_dialog.updated_notif'));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); }}>
        {(externalOpen === undefined) && (
          <DialogTrigger asChild>
            {iconOnly ? (
              <button
                type="button"
                className="p-1 hover:bg-muted/50 rounded transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Pencil className="w-4 h-4 text-muted-foreground" />
              </button>
            ) : (
              <Button variant="ghost" size="sm" className="w-fit border border-input">
                <Plus className="w-4 h-4 mr-2" />
                {t('components.custom_metrics_dialog.add_metric')}
              </Button>
            )}
          </DialogTrigger>
        )}
        <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[85vh] flex flex-col [&>div]:scrollbar-none [&>div]:[-webkit-overflow-scrolling:touch]" aria-describedby={undefined}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => { if (metricToDelete !== null) return; e.preventDefault(); }}
          onInteractOutside={(e) => { if (metricToDelete !== null) return; e.preventDefault(); }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editMode ? t('components.custom_metrics_dialog.title_edit') : t('components.custom_metrics_dialog.title_create', { current: metricsToUse.length, max: MAX_CUSTOM_METRICS })}
            </DialogTitle>
          </DialogHeader>

          {editMode ? (
            // Edit mode - only show form fields for editing
            <>
              <div className="flex-1 overflow-y-auto pr-2 scrollbar-none [-webkit-overflow-scrolling:touch]">
                <div className="space-y-4 p-4 border-2 border-dashed rounded-lg">
                  {/* Name field with emoji picker */}
                  <div
                    className="space-y-2 animate-in fade-in slide-in-from-top-2"
                    style={{
                      animationDuration: '400ms',
                      animationDelay: '0ms',
                      animationFillMode: 'both'
                    }}
                  >
                    <Label>{t('components.custom_metrics_dialog.name_label')}</Label>
                    <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
                      <EmojiPicker
                        selectedEmoji={newMetric.emoji}
                        onEmojiSelect={(emoji) => setNewMetric({ ...newMetric, emoji })}
                      />
                      <Input
                        placeholder={newMetric.metricType === "boolean" ? BOOLEAN_EXAMPLES[placeholderIndex] : PLACEHOLDER_EXAMPLES[placeholderIndex]}
                        maxLength={23}
                        value={newMetric.name}
                        onChange={(e) => {
                          setNewMetric({ ...newMetric, name: e.target.value.replace(/[^A-Za-z0-9 ]/g, '') });
                          setValidationError("");
                        }}
                        className="text-[0.56em]"
                      />
                    </div>
                  </div>

                  {/* Metric Type */}
                  <div
                    className="space-y-2 animate-in fade-in slide-in-from-top-2"
                    style={{
                      animationDuration: '400ms',
                      animationDelay: '50ms',
                      animationFillMode: 'both'
                    }}
                  >
                    <Label>{t('components.custom_metrics_dialog.type_label')}</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={newMetric.metricType === "numeric" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setNewMetric({ ...newMetric, metricType: "numeric" })}
                      >
                        {t('components.custom_metrics_dialog.type_number')}
                      </Button>
                      <Button
                        type="button"
                        variant={newMetric.metricType === "boolean" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setNewMetric({ ...newMetric, metricType: "boolean" })}
                      >
                        {t('components.custom_metrics_dialog.type_yes_no')}
                      </Button>
                    </div>
                  </div>

                  {/* Goal and Unit inline for numeric metrics */}
                  {newMetric.metricType === "numeric" && (
                    <>
                      <div
                        className="space-y-2 animate-in fade-in slide-in-from-top-2"
                        style={{
                          animationDuration: '400ms',
                          animationDelay: '100ms',
                          animationFillMode: 'both'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <Label className={newMetric.isRelative ? "opacity-50" : ""}>{t('components.custom_metrics_dialog.out_of')}</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setNewMetric({ ...newMetric, isRelative: !newMetric.isRelative })}
                          >
                            {newMetric.isRelative ? (
                              <Plus className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        {!newMetric.isRelative && (
                          <Input
                            type="number"
                            placeholder={t('forms.placeholders.e_g_100')}
                            min="1"
                            step="1"
                            value={newMetric.goal}
                            onChange={(e) => setNewMetric({ ...newMetric, goal: e.target.value })}
                          />
                        )}
                      </div>
                      <div
                        className="space-y-2 animate-in fade-in slide-in-from-top-2"
                        style={{
                          animationDuration: '400ms',
                          animationDelay: '150ms',
                          animationFillMode: 'both'
                        }}
                      >
                        <Label>{t('components.custom_metrics_dialog.unit_label')}</Label>
                        <Input
                          placeholder={UNIT_EXAMPLES[placeholderIndex]}
                          maxLength={20}
                          value={newMetric.unit}
                          onChange={(e) => setNewMetric({ ...newMetric, unit: e.target.value })}
                        />
                      </div>

                      {/* Color Picker for numeric metrics in edit mode */}
                      {!hideColorPicker && (
                        <div
                          className="space-y-2 animate-in fade-in slide-in-from-top-2"
                          style={{
                            animationDuration: '400ms',
                            animationDelay: '200ms',
                            animationFillMode: 'both'
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <Label>{t('components.custom_metrics_dialog.chart_color_label')}</Label>
                            {!isPremium && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs gap-1"
                                onClick={() => {
                                  setOpen(false);
                                  navigate("/upgrade");
                                }}
                              >
                                <Crown className="w-3 h-3" />
                                {t('components.custom_metrics_dialog.pro_label')}
                              </Button>
                            )}
                          </div>

                          {isPremium ? (
                            <>
                              <div className="space-y-2">
                                <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                  <div
                                    className="w-full h-10 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                    style={{
                                      background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                    }}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = e.clientX - rect.left;
                                      const percentage = x / rect.width;
                                      const targetHue = Math.round(percentage * 360);
                                      const clampedHue = Math.max(0, Math.min(360, targetHue));

                                      // Lerp to target
                                      const currentHue = newMetric.hue;
                                      const steps = 10;
                                      let step = 0;
                                      const animate = () => {
                                        step++;
                                        const progress = step / steps;
                                        const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                                        const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                        setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                        if (step < steps) {
                                          requestAnimationFrame(animate);
                                        }
                                      };
                                      requestAnimationFrame(animate);
                                    }}
                                    onTouchStart={(e) => {
                                      e.preventDefault();
                                      const touch = e.touches[0];
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = touch.clientX - rect.left;
                                      const percentage = x / rect.width;
                                      const targetHue = Math.round(percentage * 360);
                                      const clampedHue = Math.max(0, Math.min(360, targetHue));

                                      // Lerp to target
                                      const currentHue = newMetric.hue;
                                      const steps = 10;
                                      let step = 0;
                                      const animate = () => {
                                        step++;
                                        const progress = step / steps;
                                        const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                                        const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                        setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                        if (step < steps) {
                                          requestAnimationFrame(animate);
                                        }
                                      };
                                      requestAnimationFrame(animate);
                                    }}
                                  />
                                  <div className="relative -mt-6">
                                    <input
                                      type="range"
                                      min="0"
                                      max="360"
                                      value={newMetric.hue}
                                      onChange={(e) => setNewMetric({ ...newMetric, hue: parseInt(e.target.value), presetType: "none" })}
                                      className="w-full cursor-pointer touch-manipulation"
                                      style={{
                                        height: '56px',
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        WebkitTapHighlightColor: 'transparent',
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                      }}
                                    />
                                  </div>
                                </div>
                                {/* Saturation/Vibrancy slider - from white to vivid */}
                                <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                  <div
                                    className="w-full h-8 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                    style={{
                                      background: `linear-gradient(to right, white, hsl(${newMetric.hue}, 100%, 50%))`
                                    }}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = e.clientX - rect.left;
                                      const percentage = x / rect.width;
                                      const targetSat = Math.round(percentage * 100);
                                      setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                    }}
                                    onTouchStart={(e) => {
                                      e.preventDefault();
                                      const touch = e.touches[0];
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const x = touch.clientX - rect.left;
                                      const percentage = x / rect.width;
                                      const targetSat = Math.round(percentage * 100);
                                      setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                    }}
                                  />
                                  <div className="relative -mt-5">
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={newMetric.saturation}
                                      onChange={(e) => setNewMetric({ ...newMetric, saturation: parseInt(e.target.value), presetType: "none" })}
                                      className="w-full cursor-pointer touch-manipulation"
                                      style={{
                                        height: '48px',
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        background: 'transparent',
                                        outline: 'none',
                                        WebkitTapHighlightColor: 'transparent',
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                      }}
                                    />
                                  </div>
                                </div>
                                  <style>{`
                                input[type="range"]::-webkit-slider-thumb {
                                  -webkit-appearance: none;
                                  appearance: none;
                                  width: 40px;
                                  height: 40px;
                                  border-radius: 50%;
                                  background: white;
                                  cursor: grab;
                                  border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                  -webkit-tap-highlight-color: transparent;
                                  -webkit-touch-callout: none;
                                  -webkit-user-select: none;
                                  user-select: none;
                                }
                                input[type="range"]::-webkit-slider-thumb:active {
                                  cursor: grabbing;
                                  box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                  transform: scale(1.05);
                                }
                                input[type="range"]::-moz-range-thumb {
                                  width: 40px;
                                  height: 40px;
                                  border-radius: 50%;
                                  background: white;
                                  cursor: grab;
                                  border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                  -moz-user-select: none;
                                  user-select: none;
                                }
                                input[type="range"]::-moz-range-thumb:active {
                                  cursor: grabbing;
                                  box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                  transform: scale(1.05);
                                }
                              `}</style>
                              </div>
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('components.custom_metrics_dialog.less_label')}</span>
                                {getCurrentColorPalette().map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="flex-1 h-8 rounded"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('components.custom_metrics_dialog.more_label')}</span>
                                <div
                                  className="w-8 h-8 rounded ml-1 transition-all duration-300 ease-in-out"
                                  style={{
                                    backgroundColor: 'hsl(0, 84%, 60%)',
                                    opacity: newMetric.inverse ? 1 : 0,
                                    transform: newMetric.inverse ? 'scale(1)' : 'scale(0.8)',
                                    width: newMetric.inverse ? '32px' : '0px',
                                    marginLeft: newMetric.inverse ? '4px' : '0px',
                                  }}
                                  title={t('components.custom_metrics_dialog.over_title')}
                                />
                              </div>
                              {/* Preset buttons for premium users */}
                              <div className="space-y-2 mt-3">
                                <div className="text-xs text-muted-foreground">{t('ui.text.or_use_preset')}</div>
                                <div className="flex flex-col gap-1">
                                  <Button
                                    variant={newMetric.presetType === "red-to-green" ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "red-to-green" ? "none" : "red-to-green" })}
                                    className="text-xs h-8 w-full"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-0.5">
                                        {[1, 2, 3, 4].map((level) => (
                                          <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: redToGreenPreset[level] }} />
                                        ))}
                                      </div>
                                      <span>{t('ui.text.red_to_green')}</span>
                                    </div>
                                  </Button>
                                  <Button
                                    variant={newMetric.presetType === "green-to-red" ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "green-to-red" ? "none" : "green-to-red" })}
                                    className="text-xs h-8 w-full"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-0.5">
                                        {[1, 2, 3, 4].map((level) => (
                                          <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: greenToRedPreset[level] }} />
                                        ))}
                                      </div>
                                      <span>{t('ui.text.green_to_red')}</span>
                                    </div>
                                  </Button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_less')}</span>
                                {getCurrentColorPalette().map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="flex-1 h-8 rounded"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_more')}</span>
                                {newMetric.inverse && (
                                  <div
                                    className="w-8 h-8 rounded ml-1"
                                    style={{ backgroundColor: 'hsl(0, 84%, 60%)' }}
                                    title="Over"
                                  />
                                )}
                              </div>
                              {/* Preset buttons for non-premium users (still show preview) */}
                              <div className="space-y-2 mt-3">
                                <div className="text-xs text-muted-foreground">{t('ui.text.or_use_preset')}</div>
                                <div className="flex flex-col gap-1">
                                  <Button
                                    variant={newMetric.presetType === "red-to-green" ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "red-to-green" ? "none" : "red-to-green" })}
                                    className="text-xs h-8 w-full"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-0.5">
                                        {[1, 2, 3, 4].map((level) => (
                                          <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: redToGreenPreset[level] }} />
                                        ))}
                                      </div>
                                      <span>{t('ui.text.red_to_green')}</span>
                                    </div>
                                  </Button>
                                  <Button
                                    variant={newMetric.presetType === "green-to-red" ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "green-to-red" ? "none" : "green-to-red" })}
                                    className="text-xs h-8 w-full"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-0.5">
                                        {[1, 2, 3, 4].map((level) => (
                                          <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: greenToRedPreset[level] }} />
                                        ))}
                                      </div>
                                      <span>{t('ui.text.green_to_red')}</span>
                                    </div>
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Inverse Mode Toggle for numeric - show when not relative */}
                      {!newMetric.isRelative && (
                        <div
                          className="space-y-2 animate-in fade-in slide-in-from-top-2"
                          style={{
                            animationDuration: '400ms',
                            animationDelay: '250ms',
                            animationFillMode: 'both'
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>
                                {t('components.custom_metrics_dialog.over_x_bad', { val: newMetric.goal || "X" })}
                              </Label>
                              <div className="text-xs text-muted-foreground">
                                {t('components.custom_metrics_dialog.fade_red_past', { val: newMetric.goal || "X" })}
                              </div>
                            </div>
                            <Switch
                              checked={newMetric.inverse}
                              onCheckedChange={(checked) => setNewMetric({ ...newMetric, inverse: checked })}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Inverse Mode Toggle for boolean */}
                  {newMetric.metricType === "boolean" && (
                    <>
                      <div
                        className="space-y-2 animate-in fade-in slide-in-from-top-2"
                        style={{
                          animationDuration: '400ms',
                          animationDelay: '100ms',
                          animationFillMode: 'both'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>
                              {t('components.custom_metrics_dialog.inverse_yes_bad')}
                            </Label>
                            <div className="text-xs text-muted-foreground">
                              {t('components.custom_metrics_dialog.inverse_yes_desc')}
                            </div>
                          </div>
                          <Switch
                            checked={newMetric.inverse}
                            onCheckedChange={(checked) => setNewMetric({ ...newMetric, inverse: checked })}
                          />
                        </div>
                      </div>

                      {/* Color Picker for boolean metrics in edit mode */}
                      {!hideColorPicker && (
                        <div
                          className="space-y-2 animate-in fade-in slide-in-from-top-2"
                          style={{
                            animationDuration: '400ms',
                            animationDelay: '150ms',
                            animationFillMode: 'both'
                          }}
                        >
                          {!newMetric.inverse && (
                            <div className="flex items-center justify-between">
                              <Label>{t('ui.text.chart_color')}</Label>
                              {!isPremium && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs gap-1"
                                  onClick={() => {
                                    setOpen(false);
                                    navigate("/upgrade");
                                  }}
                                >
                                  <Crown className="w-3 h-3" />
                                  Pro
                                </Button>
                              )}
                            </div>
                          )}

                          {isPremium ? (
                            <>
                              {!newMetric.inverse && (
                                <div className="space-y-2">
                                  <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                    <div
                                      className="w-full h-10 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                      style={{
                                        background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                      }}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const percentage = x / rect.width;
                                        const targetHue = Math.round(percentage * 360);
                                        const clampedHue = Math.max(0, Math.min(360, targetHue));

                                        const currentHue = newMetric.hue;
                                        const steps = 10;
                                        let step = 0;
                                        const animate = () => {
                                          step++;
                                          const progress = step / steps;
                                          const easedProgress = 1 - Math.pow(1 - progress, 3);
                                          const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                          setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                          if (step < steps) {
                                            requestAnimationFrame(animate);
                                          }
                                        };
                                        requestAnimationFrame(animate);
                                      }}
                                      onTouchStart={(e) => {
                                        e.preventDefault();
                                        const touch = e.touches[0];
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = touch.clientX - rect.left;
                                        const percentage = x / rect.width;
                                        const targetHue = Math.round(percentage * 360);
                                        const clampedHue = Math.max(0, Math.min(360, targetHue));

                                        const currentHue = newMetric.hue;
                                        const steps = 10;
                                        let step = 0;
                                        const animate = () => {
                                          step++;
                                          const progress = step / steps;
                                          const easedProgress = 1 - Math.pow(1 - progress, 3);
                                          const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                          setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                          if (step < steps) {
                                            requestAnimationFrame(animate);
                                          }
                                        };
                                        requestAnimationFrame(animate);
                                      }}
                                    />
                                    <div className="relative -mt-6">
                                      <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={newMetric.hue}
                                        onChange={(e) => setNewMetric({ ...newMetric, hue: parseInt(e.target.value), presetType: "none" })}
                                        className="w-full cursor-pointer touch-manipulation"
                                        style={{
                                          height: '56px',
                                          appearance: 'none',
                                          WebkitAppearance: 'none',
                                          background: 'transparent',
                                          outline: 'none',
                                          WebkitTapHighlightColor: 'transparent',
                                        }}
                                        onTouchStart={(e) => {
                                          e.stopPropagation();
                                        }}
                                      />
                                    </div>
                                  </div>
                                  {/* Saturation/Vibrancy slider - from white to vivid */}
                                  <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                    <div
                                      className="w-full h-8 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                      style={{
                                        background: `linear-gradient(to right, white, hsl(${newMetric.hue}, 100%, 50%))`
                                      }}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const percentage = x / rect.width;
                                        const targetSat = Math.round(percentage * 100);
                                        setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                      }}
                                      onTouchStart={(e) => {
                                        e.preventDefault();
                                        const touch = e.touches[0];
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = touch.clientX - rect.left;
                                        const percentage = x / rect.width;
                                        const targetSat = Math.round(percentage * 100);
                                        setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                      }}
                                    />
                                    <div className="relative -mt-5">
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={newMetric.saturation}
                                        onChange={(e) => setNewMetric({ ...newMetric, saturation: parseInt(e.target.value), presetType: "none" })}
                                        className="w-full cursor-pointer touch-manipulation"
                                        style={{
                                          height: '48px',
                                          appearance: 'none',
                                          WebkitAppearance: 'none',
                                          background: 'transparent',
                                          outline: 'none',
                                          WebkitTapHighlightColor: 'transparent',
                                        }}
                                        onTouchStart={(e) => {
                                          e.stopPropagation();
                                        }}
                                      />
                                    </div>
                                  </div>
                                    <style>{`
                                  input[type="range"]::-webkit-slider-thumb {
                                    -webkit-appearance: none;
                                    appearance: none;
                                    width: 40px;
                                    height: 40px;
                                    border-radius: 50%;
                                    background: white;
                                    cursor: grab;
                                    border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                    -webkit-tap-highlight-color: transparent;
                                    -webkit-touch-callout: none;
                                    -webkit-user-select: none;
                                    user-select: none;
                                  }
                                  input[type="range"]::-webkit-slider-thumb:active {
                                    cursor: grabbing;
                                    box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                    transform: scale(1.05);
                                  }
                                  input[type="range"]::-moz-range-thumb {
                                    width: 40px;
                                    height: 40px;
                                    border-radius: 50%;
                                    background: white;
                                    cursor: grab;
                                    border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                    -moz-user-select: none;
                                    user-select: none;
                                  }
                                  input[type="range"]::-moz-range-thumb:active {
                                    cursor: grabbing;
                                    box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                    transform: scale(1.05);
                                  }
                                `}</style>
                                </div>
                              )}

                              {/* Yes/No squares preview for boolean metrics */}
                              <div className="flex gap-2 items-center justify-center">
                                <div className="text-center">
                                  <div
                                    className="w-16 h-16 rounded mx-auto mb-1"
                                    style={{ backgroundColor: newMetric.inverse ? 'hsl(0, 84%, 60%)' : `hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%)` }}
                                  />
                                  <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.yes_label')}</span>
                                </div>
                                <div className="text-center">
                                  <div
                                    className="w-16 h-16 rounded mx-auto mb-1"
                                    style={{ backgroundColor: theme === 'light' ? 'hsl(0, 0%, 85%)' : 'hsl(0, 0%, 25%)' }}
                                  />
                                  <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.no_label')}</span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Yes/No squares preview for boolean metrics */}
                              <div className="flex gap-2 items-center justify-center">
                                <div className="text-center">
                                  <div
                                    className="w-16 h-16 rounded mx-auto mb-1"
                                    style={{ backgroundColor: newMetric.inverse ? 'hsl(0, 84%, 60%)' : `hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%)` }}
                                  />
                                  <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.yes_label')}</span>
                                </div>
                                <div className="text-center">
                                  <div
                                    className="w-16 h-16 rounded mx-auto mb-1"
                                    style={{ backgroundColor: theme === 'light' ? 'hsl(0, 0%, 85%)' : 'hsl(0, 0%, 25%)' }}
                                  />
                                  <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.no_label')}</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Additive Mode Toggle - Only for numeric metrics */}
                  {newMetric.metricType === "numeric" && (
                    <div
                      className="space-y-2 pt-4 animate-in fade-in slide-in-from-top-2"
                      style={{
                        animationDuration: '400ms',
                        animationDelay: '300ms',
                        animationFillMode: 'both'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5 max-w-[70%]">
                          <Label>
                            {t('components.custom_metrics_dialog.additive_toggle_label')}
                          </Label>
                          <div className="text-xs text-muted-foreground">
                            {t('components.custom_metrics_dialog.additive_toggle_desc')}
                          </div>
                        </div>
                        <Switch
                          checked={newMetric.isAdditive}
                          onCheckedChange={(checked) => setNewMetric({ ...newMetric, isAdditive: checked })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {validationError && (
                <p className="text-sm text-destructive px-4">{validationError}</p>
              )}

              {/* Done Button */}
              <div className="flex-shrink-0 pt-4 border-t space-y-2">
                <button
                  onClick={() => {
                    setOpen(false);
                    onClose?.();
                    setTimeout(() => setShowMetricsTour(true), 150);
                  }}
                  className="w-full text-[10px] text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors flex items-center justify-center gap-1"
                >
                  {t('components.custom_metrics_dialog.guide_button')}
                </button>
                <Button onClick={() => {
                  // Validate
                  if (!newMetric.name.trim()) {
                    setValidationError(t('components.custom_metrics_dialog.name_required'));
                    return;
                  }
                  // Only validate goal for numeric metrics that are NOT relative
                  if (newMetric.metricType === "numeric" && !newMetric.isRelative) {
                    const goalValue = parseFloat(newMetric.goal);
                    if (isNaN(goalValue) || goalValue < 0) {
                      setValidationError(t('components.custom_metrics_dialog.goal_invalid'));
                      return;
                    }
                  }

                  // Call the callback with edited values
                  onSaveEdit?.({
                    name: newMetric.name,
                    emoji: newMetric.emoji,
                    unit: newMetric.unit,
                    goal: newMetric.goal,
                    inverse: newMetric.inverse,
                    metricType: newMetric.metricType,
                    isAdditive: newMetric.isAdditive,
                    isRelative: newMetric.isRelative,
                  });

                  setOpen(false);
                }} className="w-full">
                  {t('components.custom_metrics_dialog.done_button')}
                </Button>
              </div>
            </>
          ) : (
            // Normal mode - show existing metrics list and add form
            <>
              {/* Existing Metrics List - Separate Scroll View */}
              {customMetrics.length > 0 && (
                <div className="flex-shrink-0">
                  <h3 className="text-sm font-medium mb-2">{t('components.custom_metrics_dialog.your_metrics_title')}</h3>
                  <div className="relative">
                    <div
                      ref={metricsScrollRef}
                      className={`overflow-y-auto pr-2 space-y-2 border rounded-lg p-2 bg-muted/20 scrollbar-none [-webkit-overflow-scrolling:touch] ${metricsToUse.length >= MAX_CUSTOM_METRICS ? 'max-h-[300px]' : 'max-h-[200px]'}`}
                      onScroll={(e) => {
                        const target = e.currentTarget;
                        setMetricsScrollTop(target.scrollTop);
                        setMetricsScrollHeight(target.scrollHeight);
                        setMetricsClientHeight(target.clientHeight);
                      }}
                    >
                      {customMetrics.map((metric) => (
                        <div key={metric.id} className={`flex items-center gap-4 p-3 border rounded-lg bg-background ${editingMetricKey === metric.metric_key ? 'ring-2 ring-primary' : ''}`}>
                          <span className="text-2xl">{metric.metric_emoji}</span>
                          <div className="flex-1">
                            <div className="font-semibold">{metric.metric_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {metric.metric_type === "boolean" ? "Yes/No" : metric.metric_unit}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEditMetric(metric)}
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMetricToDelete(metric.metric_key)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {/* Gradient overlay to indicate scrollable content - only show when not at top/bottom */}
                    {metricsScrollTop > 10 && (
                      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-background to-transparent pointer-events-none rounded-t-lg" />
                    )}
                    {metricsScrollHeight > metricsClientHeight && metricsScrollTop < (metricsScrollHeight - metricsClientHeight - 10) && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none rounded-b-lg" />
                    )}
                  </div>
                </div>
              )}

              {/* Add New Metric Form / Edit Metric Form - Separate Scroll View */}
              {(customMetrics.length < 10 || editingMetricKey) && (
                <div className="flex flex-col">
                  {editingMetricKey ? (
                    <div className="flex items-center justify-between gap-2 mb-2 w-full p-2 -mx-2">
                      <h4 className="font-semibold">{t('components.custom_metrics_dialog.title_edit')}</h4>
                      <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="h-8 w-8 p-0">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAddMetricExpanded(!isAddMetricExpanded)}
                      className="flex items-center justify-between gap-2 mb-2 w-full p-2 -mx-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer"
                    >
                      <h4 className="font-semibold">{t('components.custom_metrics_dialog.add_new_metric_title')}</h4>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 flex items-center justify-center">
                          {isAddMetricExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                    </button>
                  )}

                  {(isAddMetricExpanded || editingMetricKey) && (
                    <>
                      <div className="overflow-y-auto pr-2 scrollbar-none [-webkit-overflow-scrolling:touch]">
                        <div className="flex flex-col p-4 border-2 border-dashed rounded-lg">
                          {/* Name field with emoji picker - Always visible */}
                          <div className="space-y-2">
                            <Label>{t('components.custom_metrics_dialog.name_label')}</Label>
                            <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
                              <EmojiPicker
                                selectedEmoji={newMetric.emoji}
                                onEmojiSelect={(emoji) => setNewMetric({ ...newMetric, emoji })}
                              />
                              <Input
                                placeholder={!hasSelectedType ? COMBINED_EXAMPLES[placeholderIndex % COMBINED_EXAMPLES.length] : (typeSelection === "boolean" ? BOOLEAN_EXAMPLES[placeholderIndex % BOOLEAN_EXAMPLES.length] : PLACEHOLDER_EXAMPLES[placeholderIndex % PLACEHOLDER_EXAMPLES.length])}
                                maxLength={23}
                                value={newMetric.name}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^A-Za-z0-9 ]/g, '');
                                  setNewMetric({ ...newMetric, name: value });
                                  setValidationError("");
                                  setHasUnsavedMetricWarning(false);
                                  if (value.trim().length > 0 && !hasTypedName) {
                                    setHasTypedName(true);
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Metric Type - Show after name is entered */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: hasTypedName ? '200px' : '0px',
                              opacity: hasTypedName ? 1 : 0,
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <Label>{t('components.custom_metrics_dialog.type_label')}</Label>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant={hasSelectedType && typeSelection === "numeric" ? "default" : "outline"}
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setNewMetric({ ...newMetric, metricType: "numeric" });
                                    setTypeSelection("numeric");
                                    setHasSelectedType(true);
                                  }}
                                >
                                  {t('components.custom_metrics_dialog.type_number')}
                                </Button>
                                <Button
                                  type="button"
                                  variant={hasSelectedType && typeSelection === "boolean" ? "default" : "outline"}
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setNewMetric({ ...newMetric, metricType: "boolean" });
                                    setTypeSelection("boolean");
                                    setHasSelectedType(true);
                                  }}
                                >
                                  {t('components.custom_metrics_dialog.type_yes_no')}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Unit field for numeric metrics - Show after type is selected */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: (hasSelectedType && typeSelection === "numeric") ? '200px' : '0px',
                              opacity: (hasSelectedType && typeSelection === "numeric") ? 1 : 0,
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <Label>{t('components.custom_metrics_dialog.unit_label')}</Label>
                              <Input
                                placeholder={UNIT_EXAMPLES[placeholderIndex % UNIT_EXAMPLES.length]}
                                maxLength={20}
                                value={newMetric.unit}
                                onChange={(e) => {
                                  setNewMetric({ ...newMetric, unit: e.target.value });
                                  if (e.target.value.trim().length > 0 && !hasEnteredUnit) {
                                    setHasEnteredUnit(true);
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Goal with X/+ toggle for numeric metrics - Show after unit is entered */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: (hasSelectedType && typeSelection === "numeric" && hasEnteredUnit) ? '200px' : '0px',
                              opacity: (hasSelectedType && typeSelection === "numeric" && hasEnteredUnit) ? 1 : 0,
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <Label className={newMetric.isRelative ? "opacity-30" : ""}>{t('components.custom_metrics_dialog.out_of')}</Label>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    setNewMetric({ ...newMetric, isRelative: !newMetric.isRelative });
                                    if (!hasInteractedWithGoal) setHasInteractedWithGoal(true);
                                  }}
                                >
                                  {newMetric.isRelative ? (
                                    <Plus className="w-4 h-4" />
                                  ) : (
                                    <X className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                              {!newMetric.isRelative && (
                                <Input
                                  type="number"
                                  placeholder={t('forms.placeholders.e_g_100')}
                                  min="1"
                                  step="1"
                                  value={newMetric.goal}
                                  onChange={(e) => {
                                    setNewMetric({ ...newMetric, goal: e.target.value });
                                    if (!hasInteractedWithGoal) setHasInteractedWithGoal(true);
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Inverse Mode Toggle for numeric - show after goal interaction */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && !newMetric.isRelative) ? '200px' : '0px',
                              opacity: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && !newMetric.isRelative) ? 1 : 0,
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label>
                                    {t('components.custom_metrics_dialog.over_x_bad', { val: newMetric.goal || "X" })}
                                  </Label>
                                  <div className="text-xs text-muted-foreground">
                                    {t('components.custom_metrics_dialog.fade_red_past', { val: newMetric.goal || "X" })}
                                  </div>
                                </div>
                                <Switch
                                  checked={newMetric.inverse}
                                  onCheckedChange={(checked) => setNewMetric({ ...newMetric, inverse: checked })}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Gradient Preview for numeric metrics when hideColorPicker is true (pre-auth/onboarding) */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && hideColorPicker) ? '80px' : '0px',
                              opacity: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && hideColorPicker) ? 1 : 0,
                            }}
                          >
                            <div className="mt-4">
                              <div className="flex gap-1 items-center">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_less')}</span>
                                {getCurrentColorPalette().map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="flex-1 h-8 rounded"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                                <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_more')}</span>
                                <div
                                  className="w-8 h-8 rounded ml-1 transition-all duration-300 ease-in-out"
                                  style={{
                                    backgroundColor: 'hsl(0, 84%, 60%)',
                                    opacity: newMetric.inverse ? 1 : 0,
                                    transform: newMetric.inverse ? 'scale(1)' : 'scale(0.8)',
                                    width: newMetric.inverse ? '32px' : '0px',
                                    marginLeft: newMetric.inverse ? '4px' : '0px',
                                  }}
                                  title="Over"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Color Picker - Pro Feature - Show after goal interaction for numeric */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-out"
                            style={{
                              maxHeight: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && !hideColorPicker) ? '600px' : '0px',
                              opacity: (hasSelectedType && typeSelection === "numeric" && hasInteractedWithGoal && !hideColorPicker) ? 1 : 0,
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <Label>{t('ui.text.chart_color')}</Label>
                                {!isPremium && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs gap-1"
                                    onClick={() => {
                                      setOpen(false);
                                      navigate("/upgrade");
                                    }}
                                  >
                                    <Crown className="w-3 h-3" />
                                    Pro
                                  </Button>
                                )}
                              </div>

                              {isPremium ? (
                                <>
                                  <div className="space-y-2">
                                    <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                      <div
                                        className="w-full h-10 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                        style={{
                                          background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                        }}
                                        onClick={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const x = e.clientX - rect.left;
                                          const percentage = x / rect.width;
                                          const targetHue = Math.round(percentage * 360);
                                          const clampedHue = Math.max(0, Math.min(360, targetHue));

                                          // Lerp to target
                                          const currentHue = newMetric.hue;
                                          const steps = 10;
                                          let step = 0;
                                          const animate = () => {
                                            step++;
                                            const progress = step / steps;
                                            const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                                            const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                            setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                            if (step < steps) {
                                              requestAnimationFrame(animate);
                                            }
                                          };
                                          requestAnimationFrame(animate);
                                        }}
                                        onTouchStart={(e) => {
                                          e.preventDefault();
                                          const touch = e.touches[0];
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const x = touch.clientX - rect.left;
                                          const percentage = x / rect.width;
                                          const targetHue = Math.round(percentage * 360);
                                          const clampedHue = Math.max(0, Math.min(360, targetHue));

                                          // Lerp to target
                                          const currentHue = newMetric.hue;
                                          const steps = 10;
                                          let step = 0;
                                          const animate = () => {
                                            step++;
                                            const progress = step / steps;
                                            const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                                            const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                            setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                            if (step < steps) {
                                              requestAnimationFrame(animate);
                                            }
                                          };
                                          requestAnimationFrame(animate);
                                        }}
                                      />
                                      <div className="relative -mt-6">
                                        <input
                                          type="range"
                                          min="0"
                                          max="360"
                                          value={newMetric.hue}
                                          onChange={(e) => setNewMetric({ ...newMetric, hue: parseInt(e.target.value), presetType: "none" })}
                                          className="w-full cursor-pointer touch-manipulation"
                                          style={{
                                            height: '56px',
                                            appearance: 'none',
                                            WebkitAppearance: 'none',
                                            background: 'transparent',
                                            outline: 'none',
                                            WebkitTapHighlightColor: 'transparent',
                                          }}
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                          }}
                                        />
                                      </div>
                                    </div>
                                    {/* Saturation/Vibrancy slider - from white to vivid */}
                                    <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                      <div
                                        className="w-full h-8 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                        style={{
                                          background: `linear-gradient(to right, white, hsl(${newMetric.hue}, 100%, 50%))`
                                        }}
                                        onClick={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const x = e.clientX - rect.left;
                                          const percentage = x / rect.width;
                                          const targetSat = Math.round(percentage * 100);
                                          setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                        }}
                                        onTouchStart={(e) => {
                                          e.preventDefault();
                                          const touch = e.touches[0];
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const x = touch.clientX - rect.left;
                                          const percentage = x / rect.width;
                                          const targetSat = Math.round(percentage * 100);
                                          setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                        }}
                                      />
                                      <div className="relative -mt-5">
                                        <input
                                          type="range"
                                          min="0"
                                          max="100"
                                          value={newMetric.saturation}
                                          onChange={(e) => setNewMetric({ ...newMetric, saturation: parseInt(e.target.value), presetType: "none" })}
                                          className="w-full cursor-pointer touch-manipulation"
                                          style={{
                                            height: '48px',
                                            appearance: 'none',
                                            WebkitAppearance: 'none',
                                            background: 'transparent',
                                            outline: 'none',
                                            WebkitTapHighlightColor: 'transparent',
                                          }}
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                          }}
                                        />
                                      </div>
                                    </div>
                                      <style>{`
                                  input[type="range"]::-webkit-slider-thumb {
                                    -webkit-appearance: none;
                                    appearance: none;
                                    width: 40px;
                                    height: 40px;
                                    border-radius: 50%;
                                    background: white;
                                    cursor: grab;
                                    border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                    -webkit-tap-highlight-color: transparent;
                                    -webkit-touch-callout: none;
                                    -webkit-user-select: none;
                                    user-select: none;
                                  }
                                  input[type="range"]::-webkit-slider-thumb:active {
                                    cursor: grabbing;
                                    box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                    transform: scale(1.05);
                                  }
                                  input[type="range"]::-moz-range-thumb {
                                    width: 40px;
                                    height: 40px;
                                    border-radius: 50%;
                                    background: white;
                                    cursor: grab;
                                    border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                    -moz-user-select: none;
                                    user-select: none;
                                  }
                                  input[type="range"]::-moz-range-thumb:active {
                                    cursor: grabbing;
                                    box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                    transform: scale(1.05);
                                  }
                                `}</style>
                                  </div>
                                  <div className="flex gap-1 items-center">
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_less')}</span>
                                    {getCurrentColorPalette().map((color, idx) => (
                                      <div
                                        key={idx}
                                        className="flex-1 h-8 rounded"
                                        style={{ backgroundColor: color }}
                                      />
                                    ))}
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_more')}</span>
                                    <div
                                      className="w-8 h-8 rounded ml-1 transition-all duration-300 ease-in-out"
                                      style={{
                                        backgroundColor: 'hsl(0, 84%, 60%)',
                                        opacity: newMetric.inverse ? 1 : 0,
                                        transform: newMetric.inverse ? 'scale(1)' : 'scale(0.8)',
                                        width: newMetric.inverse ? '32px' : '0px',
                                        marginLeft: newMetric.inverse ? '4px' : '0px',
                                      }}
                                      title="Over"
                                    />
                                  </div>
                                  {/* Preset buttons for premium users */}
                                  <div className="space-y-2 mt-3">
                                    <div className="text-xs text-muted-foreground">{t('ui.text.or_use_preset')}</div>
                                    <div className="flex flex-col gap-1">
                                      <Button
                                        variant={newMetric.presetType === "red-to-green" ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "red-to-green" ? "none" : "red-to-green" })}
                                        className="text-xs h-8 w-full"
                                      >
                                        <div className="flex items-center gap-2">
                                          <div className="flex gap-0.5">
                                            {[1, 2, 3, 4].map((level) => (
                                              <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: redToGreenPreset[level] }} />
                                            ))}
                                          </div>
                                          <span>{t('ui.text.red_to_green')}</span>
                                        </div>
                                      </Button>
                                      <Button
                                        variant={newMetric.presetType === "green-to-red" ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "green-to-red" ? "none" : "green-to-red" })}
                                        className="text-xs h-8 w-full"
                                      >
                                        <div className="flex items-center gap-2">
                                          <div className="flex gap-0.5">
                                            {[1, 2, 3, 4].map((level) => (
                                              <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: greenToRedPreset[level] }} />
                                            ))}
                                          </div>
                                          <span>{t('ui.text.green_to_red')}</span>
                                        </div>
                                      </Button>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex gap-1 items-center">
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_less')}</span>
                                    {getCurrentColorPalette().map((color, idx) => (
                                      <div
                                        key={idx}
                                        className="flex-1 h-8 rounded"
                                        style={{ backgroundColor: color }}
                                      />
                                    ))}
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('metrics_tour.instructions.chart_more')}</span>
                                    {newMetric.inverse && (
                                      <div
                                        className="w-8 h-8 rounded ml-1"
                                        style={{ backgroundColor: 'hsl(0, 84%, 60%)' }}
                                        title="Over"
                                      />
                                    )}
                                  </div>
                                  {/* Preset buttons */}
                                  <div className="space-y-2 mt-3">
                                    <div className="text-xs text-muted-foreground">{t('ui.text.or_use_preset')}</div>
                                  <div className="flex flex-col gap-1">
                                    <Button
                                      variant={newMetric.presetType === "red-to-green" ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "red-to-green" ? "none" : "red-to-green" })}
                                      className="text-xs h-8 w-full"
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className="flex gap-0.5">
                                          {[1, 2, 3, 4].map((level) => (
                                            <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: redToGreenPreset[level] }} />
                                          ))}
                                        </div>
                                        <span>{t('ui.text.red_to_green')}</span>
                                      </div>
                                    </Button>
                                    <Button
                                      variant={newMetric.presetType === "green-to-red" ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => setNewMetric({ ...newMetric, presetType: newMetric.presetType === "green-to-red" ? "none" : "green-to-red" })}
                                      className="text-xs h-8 w-full"
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className="flex gap-0.5">
                                          {[1, 2, 3, 4].map((level) => (
                                            <div key={level} className="w-3 h-3 rounded-sm" style={{ backgroundColor: greenToRedPreset[level] }} />
                                          ))}
                                        </div>
                                        <span>{t('ui.text.green_to_red')}</span>
                                      </div>
                                    </Button>
                                  </div>
                                </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Color Picker for Boolean Metrics - Show Yes/No squares */}
                          <div
                            style={{
                              maxHeight: (typeSelection === "boolean" && !hideColorPicker) ? '500px' : '0px',
                              opacity: (typeSelection === "boolean" && !hideColorPicker) ? 1 : 0,
                              transition: 'all 300ms ease-out',
                              overflow: 'hidden',
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              {!newMetric.inverse && (
                                <div className="flex items-center justify-between">
                                  <Label>{t('ui.text.chart_color')}</Label>
                                  {!isPremium && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs gap-1"
                                      onClick={() => {
                                        setOpen(false);
                                        navigate("/upgrade");
                                      }}
                                    >
                                      <Crown className="w-3 h-3" />
                                      Pro
                                    </Button>
                                  )}
                                </div>
                              )}

                              {isPremium ? (
                                <>
                                  {!newMetric.inverse && (
                                    <div className="space-y-2">
                                      <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                        <div
                                          className="w-full h-10 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                          style={{
                                            background: 'linear-gradient(to right, hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%))'
                                          }}
                                          onClick={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const x = e.clientX - rect.left;
                                            const percentage = x / rect.width;
                                            const targetHue = Math.round(percentage * 360);
                                            const clampedHue = Math.max(0, Math.min(360, targetHue));

                                            const currentHue = newMetric.hue;
                                            const steps = 10;
                                            let step = 0;
                                            const animate = () => {
                                              step++;
                                              const progress = step / steps;
                                              const easedProgress = 1 - Math.pow(1 - progress, 3);
                                              const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                              setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                              if (step < steps) {
                                                requestAnimationFrame(animate);
                                              }
                                            };
                                            requestAnimationFrame(animate);
                                          }}
                                          onTouchStart={(e) => {
                                            e.preventDefault();
                                            const touch = e.touches[0];
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const x = touch.clientX - rect.left;
                                            const percentage = x / rect.width;
                                            const targetHue = Math.round(percentage * 360);
                                            const clampedHue = Math.max(0, Math.min(360, targetHue));

                                            const currentHue = newMetric.hue;
                                            const steps = 10;
                                            let step = 0;
                                            const animate = () => {
                                              step++;
                                              const progress = step / steps;
                                              const easedProgress = 1 - Math.pow(1 - progress, 3);
                                              const newHue = Math.round(currentHue + (clampedHue - currentHue) * easedProgress);
                                              setNewMetric((prev) => ({ ...prev, hue: newHue, presetType: "none" }));
                                              if (step < steps) {
                                                requestAnimationFrame(animate);
                                              }
                                            };
                                            requestAnimationFrame(animate);
                                          }}
                                        />
                                        <div className="relative -mt-6">
                                          <input
                                            type="range"
                                            min="0"
                                            max="360"
                                            value={newMetric.hue}
                                            onChange={(e) => setNewMetric({ ...newMetric, hue: parseInt(e.target.value), presetType: "none" })}
                                            className="w-full cursor-pointer touch-manipulation"
                                            style={{
                                              height: '56px',
                                              appearance: 'none',
                                              WebkitAppearance: 'none',
                                              background: 'transparent',
                                              outline: 'none',
                                              WebkitTapHighlightColor: 'transparent',
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                            }}
                                          />
                                        </div>
                                      </div>
                                      {/* Saturation/Vibrancy slider - from white to vivid */}
                                      <div className="relative select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
                                        <div
                                          className="w-full h-8 rounded cursor-pointer touch-manipulation active:scale-[0.98] transition-transform"
                                          style={{
                                            background: `linear-gradient(to right, white, hsl(${newMetric.hue}, 100%, 50%))`
                                          }}
                                          onClick={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const x = e.clientX - rect.left;
                                            const percentage = x / rect.width;
                                            const targetSat = Math.round(percentage * 100);
                                            setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                          }}
                                          onTouchStart={(e) => {
                                            e.preventDefault();
                                            const touch = e.touches[0];
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const x = touch.clientX - rect.left;
                                            const percentage = x / rect.width;
                                            const targetSat = Math.round(percentage * 100);
                                            setNewMetric({ ...newMetric, saturation: Math.max(0, Math.min(100, targetSat)), presetType: "none" });
                                          }}
                                        />
                                        <div className="relative -mt-5">
                                          <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={newMetric.saturation}
                                            onChange={(e) => setNewMetric({ ...newMetric, saturation: parseInt(e.target.value), presetType: "none" })}
                                            className="w-full cursor-pointer touch-manipulation"
                                            style={{
                                              height: '48px',
                                              appearance: 'none',
                                              WebkitAppearance: 'none',
                                              background: 'transparent',
                                              outline: 'none',
                                              WebkitTapHighlightColor: 'transparent',
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                            }}
                                          />
                                        </div>
                                      </div>
                                        <style>{`
                                    input[type="range"]::-webkit-slider-thumb {
                                      -webkit-appearance: none;
                                      appearance: none;
                                      width: 40px;
                                      height: 40px;
                                      border-radius: 50%;
                                      background: white;
                                      cursor: grab;
                                      border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                      -webkit-tap-highlight-color: transparent;
                                      -webkit-touch-callout: none;
                                      -webkit-user-select: none;
                                      user-select: none;
                                    }
                                    input[type="range"]::-webkit-slider-thumb:active {
                                      cursor: grabbing;
                                      box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                      transform: scale(1.05);
                                    }
                                    input[type="range"]::-moz-range-thumb {
                                      width: 40px;
                                      height: 40px;
                                      border-radius: 50%;
                                      background: white;
                                      cursor: grab;
                                      border: 5px solid hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%);
                                      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                                      -moz-user-select: none;
                                      user-select: none;
                                    }
                                    input[type="range"]::-moz-range-thumb:active {
                                      cursor: grabbing;
                                      box-shadow: 0 6px 12px rgba(0,0,0,0.4);
                                      transform: scale(1.05);
                                    }
                                  `}</style>
                                    </div>
                                  )}
                                  <div className="flex gap-2 items-center justify-center">
                                    <div className="text-center">
                                      <div
                                        className="w-16 h-16 rounded mx-auto mb-1"
                                        style={{ backgroundColor: newMetric.inverse ? 'hsl(0, 84%, 60%)' : `hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%)` }}
                                      />
                                      <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.yes_label')}</span>
                                    </div>
                                    <div className="text-center">
                                      <div
                                        className="w-16 h-16 rounded mx-auto mb-1"
                                        style={{ backgroundColor: theme === 'light' ? 'hsl(0, 0%, 85%)' : 'hsl(0, 0%, 25%)' }}
                                      />
                                      <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.no_label')}</span>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div className="flex gap-2 items-center justify-center">
                                  <div className="text-center">
                                    <div
                                      className="w-16 h-16 rounded mx-auto mb-1"
                                      style={{ backgroundColor: newMetric.inverse ? 'hsl(0, 84%, 60%)' : `hsl(${newMetric.hue}, ${newMetric.saturation}%, ${100 - newMetric.saturation / 2}%)` }}
                                    />
                                    <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.yes_label')}</span>
                                  </div>
                                  <div className="text-center">
                                    <div
                                      className="w-16 h-16 rounded mx-auto mb-1"
                                      style={{ backgroundColor: theme === 'light' ? 'hsl(0, 0%, 85%)' : 'hsl(0, 0%, 25%)' }}
                                    />
                                    <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.no_label')}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Yes/No squares preview for boolean when hideColorPicker is true (pre-auth/onboarding) */}
                          <div
                            style={{
                              maxHeight: (typeSelection === "boolean" && hideColorPicker) ? '120px' : '0px',
                              opacity: (typeSelection === "boolean" && hideColorPicker) ? 1 : 0,
                              transition: 'all 300ms ease-out',
                              overflow: 'hidden',
                            }}
                          >
                            <div className="flex gap-2 items-center justify-center mt-4">
                              <div className="text-center">
                                <div
                                  className="w-16 h-16 rounded mx-auto mb-1"
                                  style={{ backgroundColor: newMetric.inverse ? 'hsl(0, 84%, 60%)' : `hsl(${newMetric.hue}, 70%, 50%)` }}
                                />
                                <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.yes_label')}</span>
                              </div>
                              <div className="text-center">
                                <div
                                  className="w-16 h-16 rounded mx-auto mb-1"
                                  style={{ backgroundColor: theme === 'light' ? 'hsl(0, 0%, 85%)' : 'hsl(0, 0%, 25%)' }}
                                />
                                <span className="text-xs text-muted-foreground">{t('components.custom_metrics_dialog.no_label')}</span>
                              </div>
                            </div>
                          </div>

                          {/* Inverse Mode Toggle for boolean */}
                          <div
                            style={{
                              maxHeight: typeSelection === "boolean" ? '120px' : '0px',
                              opacity: typeSelection === "boolean" ? 1 : 0,
                              transition: 'all 300ms ease-out',
                              overflow: 'hidden',
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label>{t('ui.text.inverse_yes_bad')}</Label>
                                  <div className="text-xs text-muted-foreground">{t('ui.text.checking_yes_turns_red_instead_of_green')}</div>
                                </div>
                                <Switch
                                  checked={newMetric.inverse}
                                  onCheckedChange={(checked) => setNewMetric({ ...newMetric, inverse: checked })}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Additive Mode Toggle - Only for numeric metrics */}
                          <div
                            style={{
                              maxHeight: (hasInteractedWithGoal && typeSelection === "numeric") ? '120px' : '0px',
                              opacity: (hasInteractedWithGoal && typeSelection === "numeric") ? 1 : 0,
                              transition: 'all 300ms ease-out',
                              overflow: 'hidden',
                            }}
                          >
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5 max-w-[70%]">
                                  <Label>{t('ui.text.add_up_daily_entries')}</Label>
                                  <div className="text-xs text-muted-foreground">{t('ui.text.turn_off_if_each_entry_replaces_the_last_like_weig')}</div>
                                </div>
                                <Switch
                                  checked={newMetric.isAdditive}
                                  onCheckedChange={(checked) => setNewMetric({ ...newMetric, isAdditive: checked })}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Screenshot Import Section - hidden during onboarding */}
                          {!hideColorPicker && (
                          <div
                            style={{
                              maxHeight: (hasInteractedWithGoal || typeSelection === "boolean") ? '2000px' : '0px',
                              opacity: (hasInteractedWithGoal || typeSelection === "boolean") ? 1 : 0,
                              transition: 'all 300ms ease-out',
                              overflow: 'visible',
                            }}
                          >
                            <div className="mt-4">
                              <ScreenshotImportSection
                                metricType={newMetric.metricType}
                                onImportData={setImportedScreenshotData}
                                importedData={importedScreenshotData}
                              />
                            </div>
                          </div>
                          )}
                        </div>
                      </div>

                      {/* Add/Save Metric Button - Outside scroll view */}
                      <div
                        className="flex-shrink-0 pt-4"
                        style={{
                          opacity: (hasInteractedWithGoal || typeSelection === "boolean") ? 1 : 0,
                          transition: 'opacity 300ms ease-out',
                          pointerEvents: (hasInteractedWithGoal || typeSelection === "boolean") ? 'auto' : 'none',
                        }}
                      >
                        {validationError && (
                          <p className="text-sm text-destructive mb-2">{validationError}</p>
                        )}
                        {editingMetricKey ? (
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={handleCancelEdit} className="flex-1">
                              {t('components.custom_metrics_dialog.cancel_button')}
                            </Button>
                            <Button onClick={handleSaveEditMetric} className="flex-1">
                              <Pencil className="w-4 h-4 mr-2" />{t('components.custom_metrics_dialog.done_button')}
                            </Button>
                          </div>
                        ) : (
                          <Button onClick={handleAddMetric} className="w-full">
                            <Plus className="w-4 h-4 mr-2" />{t('ui.text.add_metric')}</Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Done Button and Delete Message - Always Visible at Bottom */}
              <div className="flex-shrink-0 pt-4 border-t space-y-2">
                {metricsToUse.length >= MAX_CUSTOM_METRICS && (
                  <p className="text-xs text-muted-foreground text-center">{t('ui.text.delete_a_metric_to_free_up_slots')}</p>
                )}
                <button
                  onClick={() => {
                    setOpen(false);
                    onClose?.();
                    setTimeout(() => setShowMetricsTour(true), 150);
                  }}
                  className="w-full text-[10px] text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors flex items-center justify-center gap-1"
                >
                  {t('metrics.guide_nerds')}
                </button>
                <Button onClick={() => {
                  // Check if there's an unsaved metric (name filled in but not added)
                  if (newMetric.name.trim() && !hasUnsavedMetricWarning) {
                    setValidationError(t('common.unsaved_metric_warning'));
                    setHasUnsavedMetricWarning(true);
                    return;
                  }

                  // Reset warning state and close
                  setHasUnsavedMetricWarning(false);
                  setValidationError("");
                  setOpen(false);
                  onClose?.();
                }} className="w-full">
                  {t('common.done')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Metrics Tour - Render outside Dialog to ensure proper z-index stacking */}
      <MetricsTour
        isOpen={showMetricsTour}
        onClose={() => setShowMetricsTour(false)}
      />

      {/* Delete Custom Metric Confirmation Dialog */}
      <AlertDialog open={metricToDelete !== null} onOpenChange={(open) => !open && setMetricToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('components.custom_metrics_dialog.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('components.custom_metrics_dialog.delete_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('components.custom_metrics_dialog.cancel_button')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (metricToDelete) {
                  deleteMetric.mutate(metricToDelete);
                }
                setMetricToDelete(null);
              }}
            >
              {t('components.custom_metrics_dialog.delete_confirm_yes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CustomMetricsDialog;
